import React, { useEffect } from 'react';
import { ArrowUp, ArrowDown, Save, Trash2 } from 'lucide-react';
import { siteHeaderApi, headerApi, pagesApi, assetUrl } from '../../../services/api';
import type { PageData } from '../types';

const HeaderSettingsContent = () => {
  const [settings, setSettings] = React.useState<{
    id?: number;
    header_style: 'style1' | 'style2' | 'style3';
    logo_url?: string;
    button_text: string;
    button_link?: string;
    button_color?: string;
    button_hover_color?: string;
    text_color?: string;
    menu_position: 'left' | 'center' | 'right';
    animation_type?: string;
    custom_css?: string;
    is_active: boolean;
  }>({
    header_style: 'style1',
    button_text: 'Get in Touch',
    text_color: '#ffffff',
    menu_position: 'center',
    is_active: true
  });
  const [menuItems, setMenuItems] = React.useState<{
    id: number;
    page_id?: number;
    menu_text: string;
    custom_url?: string;
    menu_order: number;
    is_active: boolean;
  }[]>([]);
  const [pages, setPages] = React.useState<PageData[]>([]);
  const [uploadingLogo, setUploadingLogo] = React.useState(false);

  React.useEffect(() => {
    fetchData();
    fetchPages();
  }, []);

  React.useEffect(() => {
    try {
      const saved = localStorage.getItem('headerColors');
      if (saved) {
        const colors = JSON.parse(saved);
        setSettings(prev => ({
          ...prev,
          text_color: colors.textColor ?? prev.text_color,
          button_color: colors.buttonColor ?? prev.button_color,
          button_hover_color: colors.buttonHoverColor ?? prev.button_hover_color
        }));
      }
    } catch { void 0; }
  }, []);

  const fetchData = async () => {
    try {
      let headerRes: any = null;
      try {
        headerRes = await headerApi.get();
      } catch (err) {
        headerRes = await siteHeaderApi.get();
      }
      if (headerRes?.success && headerRes.data) {
        const incoming = headerRes.data || {};
        let savedColors: any = {};
        try {
          const saved = localStorage.getItem('headerColors');
          if (saved) savedColors = JSON.parse(saved);
        } catch { void 0; }
        setSettings(prev => ({
          ...prev,
          id: typeof incoming.id === 'number' ? incoming.id : prev.id,
          logo_url: incoming.logo_url ?? prev.logo_url,
          button_text: savedColors.buttonText ?? incoming.button_text ?? prev.button_text,
          button_link: incoming.button_link ?? prev.button_link,
          button_color: savedColors.buttonColor ?? incoming.button_color ?? prev.button_color,
          button_hover_color: savedColors.buttonHoverColor ?? incoming.button_hover_color ?? prev.button_hover_color,
          text_color: savedColors.textColor ?? incoming.text_color ?? prev.text_color,
        }));
      }
      const menuRes = await siteHeaderApi.getMenuItems();
      if (menuRes?.success && Array.isArray(menuRes.data)) {
        setMenuItems(menuRes.data as any[]);
      }
    } catch (e) { console.error(e); }
  };

  const fetchPages = async () => {
    try {
      const res = await pagesApi.getAll();
      if (res?.success) setPages(res.data);
    } catch (e) { console.error(e); }
  };

  const handleSaveSettings = async () => {
    try {
      const payload: Record<string, unknown> = {};
      if (typeof settings.id === 'number') payload.id = settings.id;
      if (settings.logo_url) payload.logo_url = settings.logo_url;
      if (typeof settings.button_text === 'string') payload.button_text = settings.button_text;
      payload.button_link = settings.button_link || '';
      if (typeof settings.button_color === 'string') payload.button_color = settings.button_color;
      if (typeof settings.button_hover_color === 'string') payload.button_hover_color = settings.button_hover_color;
      if (typeof settings.text_color === 'string') payload.text_color = settings.text_color;
      // Always include logo_url in payload if it exists
      if (settings.logo_url !== undefined) payload.logo_url = settings.logo_url;
      try {
        await headerApi.update(payload);
      } catch (err1) {
        try {
          await siteHeaderApi.update(payload as any);
        } catch (err2) {
          const msg = err2 instanceof Error ? err2.message : String(err2);
          if (msg.includes('API Error 404') || msg.toLowerCase().includes('cannot put')) {
            await siteHeaderApi.create(payload);
          } else {
            throw err2;
          }
        }
      }
      try {
        localStorage.setItem('headerColors', JSON.stringify({
          textColor: settings.text_color,
          buttonColor: settings.button_color,
          buttonHoverColor: settings.button_hover_color,
          buttonText: settings.button_text
        }));
      } catch { void 0; }
      const btn = document.getElementById('save-header-btn');
      if (btn) {
        const originalText = btn.innerText;
        btn.innerText = 'Saved!';
        btn.style.backgroundColor = '#10b981';
        setTimeout(() => {
          btn.innerText = originalText;
          btn.style.backgroundColor = '';
        }, 2000);
      }
      console.log('Dispatching header-settings-updated event');
      window.dispatchEvent(new Event('header-settings-updated'));
      fetchData();
    } catch (e) {
      console.error(e);
      alert('Failed to save settings');
    }
  };

  const saveSettingsSilent = React.useCallback(async () => {
    const s = settings;
    try {
      const payload: Record<string, unknown> = {};
      if (typeof s.id === 'number') payload.id = s.id;
      if (typeof s.button_text === 'string') payload.button_text = s.button_text;
      payload.button_link = s.button_link || '';
      if (typeof s.button_color === 'string') payload.button_color = s.button_color;
      if (typeof s.button_hover_color === 'string') payload.button_hover_color = s.button_hover_color;
      if (typeof s.text_color === 'string') payload.text_color = s.text_color;
      try {
        await headerApi.update(payload);
      } catch {
        try {
          await siteHeaderApi.update(payload as any);
        } catch (err2) {
          const msg = err2 instanceof Error ? err2.message : String(err2);
          if (msg.includes('API Error 404') || msg.toLowerCase().includes('cannot put')) {
            await siteHeaderApi.create(payload);
          } else {
            throw err2;
          }
        }
      }
      try {
        localStorage.setItem('headerColors', JSON.stringify({
          textColor: s.text_color,
          buttonColor: s.button_color,
          buttonHoverColor: s.button_hover_color,
          buttonText: s.button_text
        }));
      } catch { void 0; }
      window.dispatchEvent(new Event('header-settings-updated'));
    } catch (e) {
      console.error('Auto-save failed', e);
    }
  }, []);

  useEffect(() => {
    const handle = setTimeout(() => {
      saveSettingsSilent();
    }, 500);
    return () => clearTimeout(handle);
  }, [settings.text_color, settings.button_color, settings.button_hover_color, settings.button_text, settings.button_link, saveSettingsSilent]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      try {
        setUploadingLogo(true);
        console.log('Uploading logo...');
        const res = await siteHeaderApi.uploadLogo(e.target.files[0]);
        console.log('Upload response:', res);
        if (res.success) {
          console.log('Setting logo_url in state:', res.url);
          setSettings(prev => ({ ...prev, logo_url: res.url }));
          // Auto-save after logo upload
          console.log('Calling handleSaveSettings...');
          await handleSaveSettings();
          alert('Logo uploaded and saved successfully!');
        } else {
          alert('Logo upload failed. Please try again.');
        }
      } catch (err) {
        console.error('Logo upload failed:', err);
        alert('Logo upload failed. Please try again.');
      } finally {
        setUploadingLogo(false);
      }
    }
  };

  const handleAddMenuItem = async (pageId: number) => {
    const page = pages.find(p => p.id === pageId);
    if (!page) return;
    try {
      const menuText = page.page_title || page.name || 'Untitled';
      await siteHeaderApi.addMenuItem({ page_id: pageId, menu_text: menuText, is_active: true });
      fetchData();
    } catch (e) { console.error(e); }
  };

  const handleRemoveMenuItem = async (id: number) => {
    try {
      await siteHeaderApi.deleteMenuItem(id);
      fetchData();
    } catch (e) { console.error(e); }
  };

  const moveItem = async (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === menuItems.length - 1)) return;
    
    const newItems = [...menuItems];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newItems[index], newItems[targetIndex]] = [newItems[targetIndex], newItems[index]];
    
    setMenuItems(newItems);
    
    const updates = newItems.map((item, idx) => ({ id: item.id, order: idx + 1 }));
    try {
      await siteHeaderApi.reorderMenuItems(updates);
    } catch (e) { console.error(e); }
  };

  const updateMenuItemField = (id: number, key: 'menu_text' | 'custom_url', value: string) => {
    setMenuItems(prev => prev.map(item => item.id === id ? { ...item, [key]: value } : item));
  };

  const handleSaveMenuItem = async (item: { id: number; menu_text: string; custom_url?: string; page_id?: number; is_active?: boolean }) => {
    try {
      await siteHeaderApi.updateMenuItem(item.id, {
        menu_text: item.menu_text,
        custom_url: item.custom_url || null,
        page_id: item.page_id || null,
        is_active: item.is_active !== false,
      });
      fetchData();
      window.dispatchEvent(new Event('header-settings-updated'));
    } catch (e) {
      console.error(e);
      alert('Failed to update menu item');
    }
  };

  return (
    <div className="p-8 space-y-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
            <h3 className="text-xl font-bold mb-4">Logo</h3>
            <div className="space-y-4 mb-8">
               <div>
                 <label className="block text-sm text-gray-400 mb-2">Logo Upload</label>
                 <div className="flex items-center gap-3">
                   {settings.logo_url && (
                     <img src={assetUrl(settings.logo_url)} alt="Logo" className="h-10 w-auto rounded bg-white/10 p-1" />
                   )}
                   <input
                     type="file"
                     onChange={handleLogoUpload}
                     disabled={uploadingLogo}
                     className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                   />
                   {uploadingLogo && (
                     <span className="text-sm text-cyan-400">Uploading...</span>
                   )}
                 </div>
               </div>
            </div>
            {/* <h3 className="text-xl font-bold mb-4">Colors</h3> */}
            <div className="space-y-4">
               {/* <div>
                 <label className="block text-sm text-gray-400 mb-2">Text/Icon Color</label>
                 <div className="flex items-center gap-3">
                   <input type="color" value={settings.text_color || '#ffffff'} onChange={(e) => setSettings({...settings, text_color: e.target.value})} className="h-10 w-20 bg-transparent border-0 rounded cursor-pointer" />
                   <input type="text" value={settings.text_color || '#ffffff'} onChange={(e) => setSettings({...settings, text_color: e.target.value})} className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                 </div>
               </div>
               <div>
                 <label className="block text-sm text-gray-400 mb-2">Button Color</label>
                 <div className="flex items-center gap-3">
                   <input type="color" value={settings.button_color || '#22d3ee'} onChange={(e) => setSettings({...settings, button_color: e.target.value})} className="h-10 w-20 bg-transparent border-0 rounded cursor-pointer" />
                   <input type="text" value={settings.button_color || '#22d3ee'} onChange={(e) => setSettings({...settings, button_color: e.target.value})} className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                 </div>
               </div>
               <div>
                 <label className="block text-sm text-gray-400 mb-2">Hover Color</label>
                 <div className="flex items-center gap-3">
                   <input type="color" value={settings.button_hover_color || '#0891b2'} onChange={(e) => setSettings({...settings, button_hover_color: e.target.value})} className="h-10 w-20 bg-transparent border-0 rounded cursor-pointer" />
                   <input type="text" value={settings.button_hover_color || '#0891b2'} onChange={(e) => setSettings({...settings, button_hover_color: e.target.value})} className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
                 </div>
               </div> */}
               <div className="pt-2 border-t border-white/10">
                 {/* <label className="block text-sm font-semibold text-gray-300 mb-2">CTA Button</label> */}
                 <div className="space-y-4">
                   <div>
                     <label className="block text-sm text-gray-400 mb-2">Button Text</label>
                     <input
                       type="text"
                       value={settings.button_text || 'Get in Touch'}
                       onChange={(e) => setSettings({ ...settings, button_text: e.target.value })}
                       className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                     />
                   </div>
                   <div>
                     <label className="block text-sm text-gray-400 mb-2">Button Action</label>
                     <div className="flex items-center gap-4">
                       <label className="flex items-center gap-2 text-sm">
                         <input
                           type="radio"
                           name="cta-action"
                           checked={!settings.button_link || settings.button_link === '#'}
                           onChange={() => setSettings({ ...settings, button_link: '#' })}
                         />
                         Contact Modal
                       </label>
                       <label className="flex items-center gap-2 text-sm">
                         <input
                           type="radio"
                           name="cta-action"
                           checked={!!settings.button_link && settings.button_link !== '#'}
                           onChange={() => setSettings({ ...settings, button_link: settings.button_link || '/' })}
                         />
                         Custom Link
                       </label>
                     </div>
                   </div>
                   {!!settings.button_link && settings.button_link !== '#' && (
                     <div>
                       <label className="block text-sm text-gray-400 mb-2">Custom Link (Path or URL)</label>
                       <input
                         type="text"
                         value={settings.button_link || ''}
                         onChange={(e) => setSettings({ ...settings, button_link: e.target.value })}
                         placeholder="/contact or https://example.com"
                         className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                       />
                     </div>
                   )}
                 </div>
               </div>
            </div>
            <div className="mt-6">
              <button id="save-header-btn" onClick={handleSaveSettings} className="w-full bg-gradient-to-r from-cyan-500 to-purple-500 py-3 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(34,211,238,0.3)] transition-all">
                Save Design
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
           <div className="bg-[#0b0c15]/50 border border-white/10 rounded-2xl p-6">
             <h3 className="text-xl font-bold mb-4">Menu Items</h3>
             <div className="mb-4 flex gap-2">
               <select className="flex-1 bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" id="page-selector">
                 <option value="">Select Page to Add...</option>
                 {pages.map(p => <option key={p.id} value={p.id}>{p.page_title || p.name || 'Untitled'}</option>)}
               </select>
               <button 
                 onClick={() => {
                   const select = document.getElementById('page-selector') as HTMLSelectElement;
                   if (select.value) handleAddMenuItem(Number(select.value));
                 }}
                 className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg"
               >
                 Add
               </button>
             </div>
             <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
               <input id="custom-link-text" type="text" placeholder="Custom Link Text" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
               <input id="custom-link-url" type="text" placeholder="/path or https://url" className="bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white" />
               <button
                 onClick={async () => {
                   const t = document.getElementById('custom-link-text') as HTMLInputElement;
                   const u = document.getElementById('custom-link-url') as HTMLInputElement;
                   const text = (t?.value || '').trim();
                   const url = (u?.value || '').trim();
                   if (!text || !url) return;
                   
                   try {
                     // 1. Save to Database
                     await siteHeaderApi.addMenuItem({
                       menu_text: text,
                       custom_url: url,
                       is_active: true
                     });
                     
                     // 2. Clear inputs and refresh
                     t.value = '';
                     u.value = '';
                     fetchData(); // Refresh the list from DB
                     window.dispatchEvent(new Event('header-settings-updated'));
                   } catch (error) {
                     console.error('Error adding custom link:', error);
                     alert('Failed to add custom link to database');
                   }
                 }}
                 className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg"
               >
                 Add Custom Link
             </button>
           </div>
            <div className="mb-2 text-xs text-gray-400 font-medium">Add pages or custom external links to your site menu.</div>

            <div className="space-y-2">
              {menuItems.map((item, idx) => (
                <div key={item.id} className="p-3 bg-white/5 rounded-lg border border-white/5 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Menu Text</label>
                      <input
                        value={item.menu_text || ''}
                        onChange={(e) => updateMenuItemField(item.id, 'menu_text', e.target.value)}
                        className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                        placeholder="Menu label"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Path / URL</label>
                      <input
                        value={item.custom_url || ''}
                        onChange={(e) => updateMenuItemField(item.id, 'custom_url', e.target.value)}
                        className="w-full bg-[#1a1a2e] border border-white/10 rounded-lg px-3 py-2 text-white"
                        placeholder={item.page_id ? 'Optional custom path override' : '/path or https://url'}
                      />
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-xs">
                      {item.page_id ? (
                        <span className="text-blue-400">Linked Page</span>
                      ) : (
                        <span className="text-gray-400">Custom Link</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                    <button onClick={() => handleSaveMenuItem(item)} className="p-1 text-green-400 hover:text-green-300" title="Save menu item"><Save size={14} /></button>
                    <button onClick={() => moveItem(idx, 'up')} disabled={idx === 0} className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ArrowUp size={14} /></button>
                    <button onClick={() => moveItem(idx, 'down')} disabled={idx === menuItems.length - 1} className="p-1 text-gray-400 hover:text-white disabled:opacity-30"><ArrowDown size={14} /></button>
                    <button onClick={() => handleRemoveMenuItem(item.id)} className="p-1 text-red-400 hover:text-red-300"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
              {menuItems.length === 0 && <div className="text-center text-gray-500 py-4">No menu items added</div>}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default HeaderSettingsContent;
